"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _button = _interopRequireDefault(require("@ux/button"));
var _textEntry = _interopRequireDefault(require("@ux/text-entry"));
var _fieldFrame = _interopRequireDefault(require("@ux/field-frame"));
var _text = _interopRequireDefault(require("@ux/text"));
var _label = require("@ux/label");
var _plus = _interopRequireDefault(require("@ux/icon/plus"));
var _minus = _interopRequireDefault(require("@ux/icon/minus"));
var _useControllableState = _interopRequireDefault(require("@ux/component-utilities/lib/use-controllable-state"));
const _excluded = ["ariaLabels", "className", "errorMessage", "helpMessage", "label", "max", "min", "onKeyDown", "size", "defaultValue", "onChange", "value", "visualSize"];
/* eslint-disable complexity */
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && Object.prototype.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 *  Qunatity Selector classname
 */
const UX_QUANTITY_SELECTOR_CLASSNAME = 'ux-quantity-selector';
const UX_QUANTITY_SEL_LABEL_FLOAT = false;

/**
* Default aria-labels
*/
const DEFAULT_ARIA_LABELS = {
  increment: 'Add',
  decrement: 'Subtract'
};

/**
 * @component QuantitySelector
 * @param {Object} props - props object
 * @param {String} props.className - additional classname
 * @param {Number} props.defaultValue - default value
 * @param {Number} props.value - current value of quantity
 * @param {Function} props.onChange - callback function to handle input change
 * @param {Function} props.onKeyDown - callback function to handle keydown
 * @param {Boolean} props.invalid - is quantity invalid
 * @param {String} props.label - label for quantity selector
 * @param {Number} props.min - minimum value for quantity selector
 * @param {Number} props.max - maximum value for quantity selector
 * @param {Boolean} [props.disabled] - disabled state
 * @param {String} props.id - id of quantity selector for a11y
 * @param {Object} props.ariaLabels - aria-labels for quantity selector
 * @param {String} [props.helpMessage] - Message for the component
 * @param {String} [props.errorMessage] - Error message for the component
 * @param {('sm' | 'md')} [props.visualSize] - Sets component size, defaults to 'md'
 * @param {Object} ref - forward ref
 * @returns {React.Component} Quantity Selector
 */
const QuantitySelector = /*#__PURE__*/_react.default.forwardRef(function QuantitySelector() {
  let _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let ref = arguments.length > 1 ? arguments[1] : undefined;
  let {
      ariaLabels,
      className,
      errorMessage,
      helpMessage,
      label,
      max = Infinity,
      min = 0,
      onKeyDown,
      size,
      defaultValue: defaultProp = '',
      onChange: onControllableChange = Function.prototype,
      value: controllableProp,
      visualSize = 'md'
    } = _ref,
    textEntryProps = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    increment,
    decrement
  } = _objectSpread(_objectSpread({}, DEFAULT_ARIA_LABELS), ariaLabels);
  const {
    disabled,
    id,
    invalid,
    required,
    labelExperimentalFloat
  } = textEntryProps || {};

  // Determine if the label should float. Small components don't support floating labels.
  const float = visualSize !== 'sm' && labelExperimentalFloat || UX_QUANTITY_SEL_LABEL_FLOAT;
  const [value, onChange] = (0, _useControllableState.default)({
    prop: controllableProp,
    defaultProp,
    onChange: onControllableChange
  });
  const helpMessageId = helpMessage && `${id}-help`;
  const errorMessageId = errorMessage && `${id}-error`;
  const _onChange = (0, _react.useCallback)((val, meta) => onChange(val, meta), [onChange]);
  const _onDecrement = (0, _react.useCallback)(ev => onChange(Number(value) - 1, {
    event: ev
  }), [onChange, value]);
  const _onIncrement = (0, _react.useCallback)(ev => onChange(Number(value) + 1, {
    event: ev
  }), [onChange, value]);

  // Determine a default size based on given min and max
  const _size = Math.max(...[max, min].reduce((acc, num) => isFinite(num) && !isNaN(num) ? acc.concat(String(num).length) : acc, []));
  const classNames = (0, _classnames.default)(`${UX_QUANTITY_SELECTOR_CLASSNAME}`, `${UX_QUANTITY_SELECTOR_CLASSNAME}--size-${visualSize}`, className);
  return /*#__PURE__*/_react.default.createElement("div", {
    role: "spinbutton",
    tabIndex: "-1",
    ref: ref,
    "data-version": "UXCORE_PLACEHOLDER_VERSION",
    className: classNames,
    onKeyDown: onKeyDown,
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": value
  }, !float && (0, _label.ensureLabelComponent)(label, {
    htmlFor: id,
    required
  }), /*#__PURE__*/_react.default.createElement(_fieldFrame.default, {
    disabled,
    invalid,
    stretch: false
  }, /*#__PURE__*/_react.default.createElement(_button.default, {
    "aria-label": decrement,
    disabled: disabled || value <= min,
    design: "control",
    onClick: _onDecrement,
    icon: /*#__PURE__*/_react.default.createElement(_minus.default, null),
    value: Number(value) - 1,
    size: visualSize
  }), /*#__PURE__*/_react.default.createElement(_textEntry.default, (0, _extends2.default)({
    "aria-describedby": [helpMessageId, errorMessageId].filter(Boolean).join(' '),
    label: float && label,
    type: "number",
    size: size || _size,
    className: `${UX_QUANTITY_SELECTOR_CLASSNAME}-input`,
    value: value,
    onChange: _onChange,
    visualSize: visualSize
  }, textEntryProps)), /*#__PURE__*/_react.default.createElement(_button.default, {
    "aria-label": increment,
    design: "control",
    onClick: _onIncrement,
    disabled: disabled || value >= max,
    icon: /*#__PURE__*/_react.default.createElement(_plus.default, null),
    value: Number(value) + 1,
    size: visualSize
  })), helpMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: helpMessage,
    as: "caption",
    id: helpMessageId,
    emphasis: "passive"
  }), errorMessage && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    text: errorMessage,
    as: "caption",
    id: errorMessageId,
    emphasis: "critical",
    role: "alert"
  }));
});

/**
 * @enum {Object} - propTypes
 * @public
*/
QuantitySelector.propTypes = _objectSpread(_objectSpread({}, _textEntry.default.propTypes), {}, {
  /** Maximum number the quantity can be selected up to used for a11y */
  max: _propTypes.default.number,
  /** Minimum number the quantity can be selected down to used for a11y */
  min: _propTypes.default.number,
  /** Optional message which appears below the input */
  helpMessage: _propTypes.default.string,
  /** Optional error message which appears below the input */
  errorMessage: _propTypes.default.string,
  /** Label for the component */
  label: _propTypes.default.node.isRequired,
  /** Screenreader labels for sub-elements */
  ariaLabels: _propTypes.default.shape({
    /** Screen reader label for "increment" double left chevron button */
    increment: _propTypes.default.string,
    /** Screen reader label for "decrement" double left chevron button */
    decrement: _propTypes.default.string
  }),
  /** Component size, default is 'md' */
  visualSize: _propTypes.default.oneOf(['sm', 'md'])
});
QuantitySelector.displayName = 'QuantitySelector';
var _default = exports.default = QuantitySelector;